import axios from "axios";
import { baseURLTechFinance } from "config/apiConfig";

const getAuthHeaders = (extraHeaders = {}) => {
  const token = localStorage.getItem("token");
  return {
    Authorization: `Bearer ${token}`,
    ...extraHeaders,
  };
};

export const fetchBatchInvoiceData = async () => {
  try {
    const response = await axios.get(`${baseURLTechFinance}/GetBatchInvoiceData`, {
      headers: getAuthHeaders({
        Accept: "text/plain",
      }),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching batch invoice data:", error);
    throw error;
  }
};

export const updateBatchInvoiceStatus = async ({ batchNo, status, reason }) => {
  try {
    const response = await axios.post(
      `${baseURLTechFinance}/UpdateBatchInvoiceStatus`,
      {
        batchNo: batchNo.toString(),
        status,
        reason,
      },
      {
        headers: getAuthHeaders({
          "Content-Type": "application/json",
        }),
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating invoice status:", error);
    throw error;
  }
};

export const updateBatchFinanceStatus = async ({ batchNo, status, reason }) => {
  try {
    const response = await fetch(`${baseURLTechFinance}/UpdateBatchFinanceStatus`, {
      method: "POST",
      headers: getAuthHeaders({
        "Content-Type": "application/json",
      }),
      body: JSON.stringify({
        batchNo: batchNo.toString(),
        status: status.toLowerCase(),
        reason: reason || "",
      }),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("API call failed:", error);
    throw error;
  }
};

export const fetchVendorSuggestions = async (query) => {
  try {
    const response = await axios.get(
      `${baseURLTechFinance}/GetServiceProvidersByName?searchText=${query}`,
      {
        headers: getAuthHeaders({
          Accept: "application/json",
        }),
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching vendor suggestions:", error);
    throw error;
  }
};

export const fetchGadgetCaseDetailsByVendor = async (vendorName) => {
  const myUrl = `${baseURLTechFinance}/GetGadgetCaseDetailsByVendor?vendorName=${encodeURIComponent(
    vendorName
  )}`;
  try {
    const response = await axios.get(myUrl, {
      headers: getAuthHeaders(),
    });
    if (response.data.status) {
      return { success: true, data: response.data.dataItems };
    } else {
      return { success: false, message: response.data.message };
    }
  } catch (error) {
    console.error("API Error:", error);
    return { success: false, message: error.message };
  }
};

export const getAllClaimDetailsPrefiled = async (aaNumber) => {
  const myUrl = `${baseURLTechFinance}/GetAllGadgetClaimDetails?aaNumber=${aaNumber}`;
  try {
    const response = await axios.get(myUrl, {
      headers: getAuthHeaders(),
    });
    if (response.data.status) {
      return { success: true, data: response.data.dataItems };
    } else {
      return { success: false, message: response.data.message };
    }
  } catch (error) {
    console.error("API Error:", error);
    return { success: false, message: error.message };
  }
};

export const GetUpdatedStatusData = async () => {
  try {
    const response = await axios.get(`${baseURLTechFinance}/GetUpdatedStatusData`, {
      headers: getAuthHeaders({
        Accept: "text/plain",
      }),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching batch invoice data:", error);
    throw error;
  }
};

export const raiseConcern = async (data) => {
  try {
    const response = await fetch(`${baseURLTechFinance}/InsertRaisedConcern`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: data,
    });

    if (!response.ok) throw new Error("Failed to raise concern");

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Error raising concern:", error);
    throw error;
  }
};

export const SaveApprovalBatchDataFlow = async (invoice) => {
  const toISODate = (dateStr) => {
    if (!dateStr || dateStr === "--" || dateStr === "null") return null;

    try {
      if (dateStr.includes("/")) {
        const [day, month, year] = dateStr.split("/");
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
      } else if (dateStr.includes("-")) {
        const [day, month, year] = dateStr.split("-");
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
      } else if (dateStr.match(/^\d{8}$/)) {
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        return `${year}-${month}-${day}`;
      }
      return null;
    } catch (e) {
      console.error("Date conversion error:", e);
      return null;
    }
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === "--") return "0";
    const num = parseFloat(value.toString().replace(/[^0-9.-]/g, ""));
    return isNaN(num) ? "0" : num.toString();
  };

  const validateAANo = (aaNo) => {
    if (!aaNo || aaNo === "--") return null;
    const cleanAA = aaNo.toString().trim();
    return cleanAA.length > 0 ? cleanAA : null;
  };

  const validateIMEI = (imei) => {
    if (!imei || imei === "--") return null;
    return imei.toString();
  };

  const payload = {
    id: invoice.id || 0,
    sellingPartner: invoice.sellingPartner || "Unknown",
    aaNo: validateAANo(invoice.aA_Number),
    imeiNo: validateIMEI(invoice.imeiNumber),
    BatchNo: invoice.BatchNo,
    creationDate: toISODate(invoice.creationDate),
    closureDate: toISODate(invoice.closureDate),
    customerName: invoice.customerName || "Unknown",
    serviceType: invoice.serviceType || "Unknown",
    brand: invoice.brand || "Unknown",
    makeModel: invoice.makeModel || "Unknown",
    repairCharges: formatNumber(invoice.repairCharges),
    chargesInclGST: formatNumber(invoice.chargesIncGST),
    total: formatNumber(invoice.total),
    invoiceStatus: invoice.invoiceStatus || "Pending",
  };

  if (!payload.aaNo) {
    console.error("Invalid AA Number:", invoice.aA_Number);
    throw new Error(`Invalid AA Number: ${invoice.aA_Number}`);
  }

  if (!payload.imeiNo) {
    console.error("Invalid IMEI Number:", invoice.imeiNumber);
    throw new Error(`Invalid IMEI Number: ${invoice.imeiNumber}`);
  }

  try {
    const response = await fetch(`${baseURLTechFinance}/SaveApprovalBatchData`, {
      method: "POST",
      headers: getAuthHeaders({
        "Content-Type": "application/json",
        Accept: "*/*",
      }),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error("API Validation Errors:", errorData.errors);
      throw new Error(`Validation failed: ${JSON.stringify(errorData.errors)}`);
    }

    return await response.json();
  } catch (error) {
    console.error("API Request Error:", error);
    throw error;
  }
};

export const GetAllApprovalBatchDatafromSubmit = async () => {
  try {
    const response = await axios.get(`${baseURLTechFinance}/GetAllApprovalBatchData`, {
      headers: getAuthHeaders({
        Accept: "text/plain",
      }),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching batch invoice data:", error);
    throw error;
  }
};

export const GetNetworkRejectData = async () => {
  try {
    const response = await axios.get(`${baseURLTechFinance}/GetRejectedInvoiceData`, {
      headers: getAuthHeaders({
        Accept: "text/plain",
      }),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching batch invoice data:", error);
    throw error;
  }
};

export const GetPartialApprovedInvoiceData = async () => {
  try {
    const response = await axios.get(`${baseURLTechFinance}/GetPartialApprovedInvoiceData`, {
      headers: getAuthHeaders({
        Accept: "text/plain",
      }),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching batch invoice data:", error);
    throw error;
  }
};

export const GetAllRejectedlBatchDatafromSubmit = async () => {
  try {
    const response = await axios.get(`${baseURLTechFinance}/GetRejectInvoiceData`, {
      headers: getAuthHeaders({
        Accept: "text/plain",
      }),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching batch invoice data:", error);
    throw error;
  }
};

export const GetAllGetQueryData = async () => {
  try {
    const response = await axios.get(`${baseURLTechFinance}/GetQueryInvoiceData`, {
      headers: getAuthHeaders({
        Accept: "text/plain",
      }),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching batch invoice data:", error);
    throw error;
  }
};

export const GetAllHoldData = async () => {
  try {
    const response = await axios.get(`${baseURLTechFinance}/GetHoldInvoiceData`, {
      headers: getAuthHeaders({
        Accept: "text/plain",
      }),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching batch invoice data:", error);
    throw error;
  }
};

export const GetRaisedConcernsNetworkForSidebar = async () => {
  try {
    const response = await axios.get(`${baseURLTechFinance}/GetRaisedConcernsNetwork`, {
      headers: getAuthHeaders({
        Accept: "text/plain",
      }),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching batch invoice data:", error);
    throw error;
  }
};

export const updateInvoiceStatusNetworkForApproved = async ({
  batchNo,
  invoiceStatus,
}) => {
  try {
    const response = await axios.post(
      `${baseURLTechFinance}/UpdateInvoiceStatusNetwork`,
      {
        batchNo: batchNo.toString(),
        invoiceStatus,
      },
      {
        headers: getAuthHeaders({
          "Content-Type": "application/json",
        }),
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating invoice status:", error);
    throw error;
  }
};

export const UpdateInvoicePartialNetwork = async ({ aaNo, invoiceStatus }) => {
  try {
    const response = await axios.post(
      `${baseURLTechFinance}/Update_InvoicePartialNetwork`,
      {
        aaNo: aaNo,
        invoiceStatus,
      },
      {
        headers: getAuthHeaders({
          "Content-Type": "application/json",
        }),
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating invoice status:", error);
    throw error;
  }
};

export const insertPaymentDetails = async (paymentData) => {
  try {
    const response = await axios.post(
      `${baseURLTechFinance}/InsertPaymentDetails`,
      paymentData,
      {
        headers: getAuthHeaders({
          "Content-Type": "application/json",
        }),
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error inserting payment details:", error);
    throw error;
  }
};

export const saveBatchPayment = async (paymentData) => {
  try {
    const response = await axios.post(
      `${baseURLTechFinance}/SaveBatchPayment`,
      paymentData,
      {
        headers: getAuthHeaders({
          "Content-Type": "application/json",
        }),
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error saving batch payment details:", error);
    throw error;
  }
};

export const bulkPaidUpload = async (file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await axios.post(`${baseURLTechFinance}/BulkPaidUpload`, formData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Error uploading bulk paid data:", error);
    throw error;
  }
};